import { AUTH_DATABASE_NAME, AUTH_STORE_NAME, OFFLINE_DATABASE_NAME, REQUESTS_STORE_NAME } from "../dbConstants";
import { api } from "../intreceptor/apiInterceptor"; // Importing api service
import { openDB } from 'idb';
import { lookupService } from '../services/lookups.service';
import { ContractorService } from '../services/contractor.service';
import { JobsiteFilters, JobsiteService } from '../services/jobsite.service';
import { userService } from '../services/user.service';
import { dbVersion } from "../db";

// Global state for auth (in-memory only)
let inMemoryAccessToken: string | null = null;
let inMemoryUser: {
  id: string;
  name: string;
  email: string;
  role: string;
  gender: string;
  mobileNumber: string;
} | null = null;
let inMemoryCurrentRole: string | null = null;

// Navigation callback
type NavigateCallback = (path: string) => void;
let navigateCallback: NavigateCallback | null = null;

// Event for auth state changes
const AUTH_STATE_CHANGE_EVENT = 'auth-state-change';
const authStateChangeEvent = new CustomEvent(AUTH_STATE_CHANGE_EVENT, { detail: {} });

// Function to sync state with React context
const syncWithAuthContext = () => {
  // Skip in service worker context
  if (typeof window === 'undefined') return;
  // This will be caught by components using the useAuth hook
  window.dispatchEvent(authStateChangeEvent);
};

// Function to navigate using the registered callback or fallback to window.location
const navigateTo = (path: string) => {
  // Skip in service worker context
  if (typeof window === 'undefined') return;
  
  if (navigateCallback) {
    try {
      navigateCallback(path);
    } catch (error) {
      console.error('Error using navigate callback:', error);
      // Fallback to direct navigation if the callback fails
      window.location.href = path;
    }
  } else {
    // Fallback to direct navigation if no callback is registered
    window.location.href = path;
  }
};

interface LoginCredentials { // Interface for login credentials
  email: string; // User's email
  password: string; // User's password
}

interface LoginResponse {
  // For successful login
  accessToken?: string;
  refreshToken?: string;
  user?: {
    id: string;
    name: string;
    email: string;
    role: string;
    gender: string;
    mobileNumber: string;
  };
  // For 2FA required
  message?: string;
  requires2FA?: boolean;
  userId?: string;
}

interface ForgotPasswordResponse {
  message: string;
  success: boolean;
}

interface ChangePasswordRequest {
  id: string;
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}

interface ChangePasswordResponse {
  message: string;
  success: boolean;
}

interface ResetPasswordRequest {
  token: string;
  password: string;
}

interface ResetPasswordResponse {
  message: string;
  success: boolean;
}

interface RefreshTokenRequest {
  refreshToken: string;
  user: {
    id: string;
    name: string;
    email: string;
    mobileNumber: string;
    gender: 'male' | 'female';
    status: 'active' | 'inactive';
    role: string;
  };
}

interface RefreshTokenResponse {
  accessToken: string;
  refreshToken: string;
  user: RefreshTokenRequest['user'];
}

interface GenerateOtpRequest {
  userId: string;
}

interface GenerateOtpResponse {
  message: string;
}

interface VerifyOtpRequest {
  userId: string;
  otp: string;
}

interface VerifyOtpResponse {
  accessToken?: string;
  refreshToken?: string;
  user?: {
    id: string;
    name: string;
    email: string;
    role: string;
    gender: string;
    mobileNumber: string;
  };
  message?: string;
  success?: boolean;
  statusCode?: number;
}

interface VerifyResetTokenResponse {
  message: string;
  code: string;
}

interface AuthServiceStatic {
  refreshPromise: Promise<string | null> | null;
}

const authServiceStatic: AuthServiceStatic = {
  refreshPromise: null
};

// Create internal functions using a closure
const createAuthService = () => {
  const saveRefreshToken = async (refreshToken: string, role: string, userData: any): Promise<void> => {
    try {
      const db = await openDB(AUTH_DATABASE_NAME, dbVersion, {
        upgrade(db) {
          if (!db.objectStoreNames.contains(AUTH_STORE_NAME)) {
            db.createObjectStore(AUTH_STORE_NAME, { keyPath: 'key' });
          }
        },
      });
      // Save refresh token
      await db.put(AUTH_STORE_NAME, { 
        tokenValue: refreshToken, 
        key: `${role}_refreshToken` 
      });
      
      // Save user data
      await db.put(AUTH_STORE_NAME, {
        userData: userData,
        key: `${role}_userData`
      });

      // Save user data
      await db.put(AUTH_STORE_NAME, {
        userData: userData,
        key: `user`
      });
    } catch (error) {
      console.error('Error saving data to IndexedDB:', error);
    }
  };

  const saveAccessToken = async (accessToken: string, role: string): Promise<void> => {
    try {
      const db = await openDB(AUTH_DATABASE_NAME, dbVersion, {
        upgrade(db) {
          if (!db.objectStoreNames.contains(AUTH_STORE_NAME)) {
            db.createObjectStore(AUTH_STORE_NAME, { keyPath: 'key' });
          }
        },
      });
      // Save refresh token
      await db.put(AUTH_STORE_NAME, { 
        tokenValue: accessToken, 
        key: `${role}_accessToken` 
      });
   
    } catch (error) {
      console.error('Error saving data to IndexedDB:', error);
    }
  };

  const getRefreshTokenFromDB = async (role: string): Promise<string | null> => {
    try {
      const db = await openDB(AUTH_DATABASE_NAME, dbVersion, {
        upgrade(db) {
          if (!db.objectStoreNames.contains(AUTH_STORE_NAME)) {
            db.createObjectStore(AUTH_STORE_NAME, { keyPath: 'key' });
          }
        },
      });
      const token = await db.get(AUTH_STORE_NAME, `${role}_refreshToken`);
      return token?.tokenValue || null;
    } catch (error) {
      console.error('Error getting refresh token from IndexedDB:', error);
      return null;
    }
  };

  const getAccessTokenFromDB = async (role: string): Promise<string | null> => {
    try {
      const db = await openDB(AUTH_DATABASE_NAME, dbVersion, {
        upgrade(db) {
          if (!db.objectStoreNames.contains(AUTH_STORE_NAME)) {
            db.createObjectStore(AUTH_STORE_NAME, { keyPath: 'key' });
          }
        },  
      });
      const token = await db.get(AUTH_STORE_NAME, `${role}_accessToken`);
      return token?.tokenValue || null;
    } catch (error) {
      console.error('Error getting access token from IndexedDB:', error);
      return null;
    }
  };

  const getUserDataFromDB = async (userKey: string): Promise<{
    id: string;
    name: string;
    email: string;
    role: string;
    gender: string;
    mobileNumber: string;
  } | null> => {
    try {
      const db = await openDB(AUTH_DATABASE_NAME, dbVersion, {
        upgrade(db) {
          if (!db.objectStoreNames.contains(AUTH_STORE_NAME)) {
            db.createObjectStore(AUTH_STORE_NAME, { keyPath: 'key' });
          }
        },
      });
      const data = await db.get(AUTH_STORE_NAME, userKey);
      return data?.userData || null;
    } catch (error) {
      console.error('Error getting user data from IndexedDB:', error);
      return null;
    }
  };

  const getUserRoleDataFromDB = async (role: string): Promise<any | null> => {
    try {
      const db = await openDB(AUTH_DATABASE_NAME, dbVersion, {
        upgrade(db) {
          if (!db.objectStoreNames.contains(AUTH_STORE_NAME)) {
            db.createObjectStore(AUTH_STORE_NAME, { keyPath: 'key' });
          }
        },
      });
      const data = await db.get(AUTH_STORE_NAME, `${role}_userData`);
      return data?.userData || null;
    } catch (error) {
      console.error('Error getting user data from IndexedDB:', error);
      return null;
    }
  };

  const clearIndexedDB = async (): Promise<void> => {
    try {
      const db = await openDB(AUTH_DATABASE_NAME, dbVersion, {
        upgrade(db) {
          if (!db.objectStoreNames.contains(AUTH_STORE_NAME)) {
            db.createObjectStore(AUTH_STORE_NAME, { keyPath: 'key' });
          }
        },
      });

      const offlineDb = await openDB(OFFLINE_DATABASE_NAME, dbVersion, {
        upgrade(db) {
          if (!db.objectStoreNames.contains(REQUESTS_STORE_NAME)) {
            db.createObjectStore(REQUESTS_STORE_NAME, { keyPath: 'key' });
          }
        },
      });

      // Clear ALL auth-related data from IndexedDB, not just current role
      await db.clear(AUTH_STORE_NAME);

      await offlineDb.clear(REQUESTS_STORE_NAME);
      const objectStoreNames = offlineDb.objectStoreNames;
      for (const name of Array.from(objectStoreNames)) {
        await offlineDb.clear(name);
      }

    } catch (error) {
      console.error('Error clearing IndexedDB:', error);
    }
  };


  const refreshToken = async (): Promise<string | null> => {
    if (!inMemoryCurrentRole) {
      await authService.clearAllData();
      // Only navigate in browser context
      if (typeof window !== 'undefined') {
        navigateTo('/login');
      }      
      return null;
    }
    
    // Get user data from IndexedDB if not in memory
    if (!inMemoryUser) {
      inMemoryUser = await getUserRoleDataFromDB(inMemoryCurrentRole);
      if (!inMemoryUser) {
        await authService.clearAllData();
        navigateTo('/login');
        return null;
      }
    }
    
    const currentRefreshToken = await getRefreshTokenFromDB(inMemoryCurrentRole);
    
    if (!currentRefreshToken) {
      await authService.clearAllData();
      navigateTo('/login');
      return null;
    }

    try {
      const data = await api.fetch<RefreshTokenResponse>('/auth/refresh', {
        method: 'POST',
        body: JSON.stringify({ refreshToken: currentRefreshToken, user: inMemoryUser }),
        requiresAuth: false,
        skipRefreshToken: true
      });

      if (!data.accessToken || !data.refreshToken || !data.user) {
        throw new Error('Invalid refresh token response');
      }

      // Update in-memory state
      inMemoryAccessToken = data.accessToken;
      inMemoryUser = data.user;
      inMemoryCurrentRole = data.user.role;

      // Force logout if user is inactive
      if (data.user.status === 'inactive') {
        await authService.clearAllData();
        navigateTo('/login');
        return null;
      }
      
      // Dispatch event to notify components
      syncWithAuthContext();

      // Save refresh token to IndexedDB
      await saveRefreshToken(data.refreshToken, data.user.role, data.user);
      await saveAccessToken(data.accessToken, data.user.role);
      return data.accessToken;
    } catch (error: any) {
      console.error('Token refresh failed:', error);
      // Only clear data and redirect if it's a real auth error
      if (error?.response?.status === 401 || error?.response?.status === 403) {
        await authService.clearAllData();
        navigateTo('/login');
      }
      return null;
    }
  };

  // Public interface
  const authService = {
    // Register a navigation callback
    registerNavigateCallback(callback: NavigateCallback) {
      navigateCallback = callback;
    },

    // Get the path for a specific role
    getRolePath(role: string): string {
      switch (role) {
        case 'admin':
          return '/dashboard';
        case 'organizer':
          return '/jobsite-management';
        case 'interRepre':
          return '/dashboard';
        default:
          return '/login';
      }
    },

    async initializeLookups() {
      await lookupService.getFundingRes();
      await lookupService.getIndustries();
      await lookupService.getProvinces();
      await lookupService.getSectors();
      await lookupService.getTrades();
      await lookupService.getJobCompStatus();
      await lookupService.getLocals();
      await ContractorService.getContractor();
      // Only fetch user data if role has permission (admin)
      const currentRole = inMemoryUser?.role;
      if (currentRole === 'admin') {
        await userService.getUsers();

        await userService.getUsersPaginated({ page: 1, limit: 10 });
      }
      await JobsiteService.getJobsites({ filters: { page: 1, limit: 10 } } as JobsiteFilters);
    },

    async login(credentials: LoginCredentials): Promise<LoginResponse> {
      try {
        
        // Clear in-memory state as well
        inMemoryAccessToken = null;
        inMemoryUser = null;
        inMemoryCurrentRole = null;
        
        const data = await api.fetch<LoginResponse>('/auth/login', {
          method: 'POST',
          body: JSON.stringify(credentials),
          requiresAuth: false
        });
        if (data.accessToken && data.refreshToken && data.user) {
          // Save all data
          await saveRefreshToken(data.refreshToken, data.user.role, data.user);
          await saveAccessToken(data.accessToken, data.user.role);

          // Update in-memory state after saves are complete
          inMemoryAccessToken = data.accessToken;
          inMemoryUser = data.user;
          inMemoryCurrentRole = data.user.role;

          // Dispatch event to notify components
          syncWithAuthContext();

          // Broadcast login event to all other tabs using localStorage
          if (typeof window !== 'undefined') {
            localStorage.setItem('auth-event', JSON.stringify({
              type: 'LOGIN',
              timestamp: Date.now()
            }));
            localStorage.removeItem('auth-event');
          }

          // Navigate to the appropriate page based on role after all operations are complete
          const path = this.getRolePath(data.user.role);
          await this.initializeLookups();

          navigateTo(path);

        }
        
        return data;
      } catch (error) {
        console.error('Login error:', error);
        throw error;
      }
    },

    async logout() {
      const isServiceWorker = typeof window === 'undefined';
      try {
        let user;
        if(isServiceWorker) {
          user = await this.getUserAsync();
        } else {
          user = await this.getUser();
        }
        if (user) {
          try {
            await api.fetch(`/auth/logout/${user.id}`, {
              method: 'POST'
            });
          } catch (error) {
            console.error('Logout error:', error);
          }
        }
      } finally {
        await this.clearAllData();

        // Broadcast logout event to all other tabs using localStorage
        if (typeof window !== 'undefined') {
          localStorage.setItem('auth-event', JSON.stringify({
            type: 'LOGOUT',
            timestamp: Date.now()
          }));
          localStorage.removeItem('auth-event');
        }

        navigateTo('/login');
      }
    },

    async clearAllData() {
      // Clear in-memory state
      inMemoryAccessToken = null;
      inMemoryUser = null;
      
      // Clear refresh token from IndexedDB
      await clearIndexedDB();
      
      inMemoryCurrentRole = null;
      
      // Clear sessionStorage
      if (typeof window !== 'undefined') {
        // Clear jobsite filters from sessionStorage
        sessionStorage.removeItem('jobsiteFilters');
        // Clear jobsite view mode from sessionStorage
        sessionStorage.removeItem('jobsiteViewMode');
        // Clear entire sessionStorage if needed
        // sessionStorage.clear();
      }
      
      // Dispatch event to notify components (only in browser context)
      if (typeof window !== 'undefined') {
        syncWithAuthContext();
      }
    },

    isAuthenticated(): boolean {
      // Check if we have an access token in memory
      if (!inMemoryAccessToken) return false;
        
      try {
        // Check if the token is expired
        const payload = JSON.parse(atob(inMemoryAccessToken.split('.')[1]));
        const expirationTime = payload.exp * 1000;
        return Date.now() < expirationTime;
      } catch (error) {
        console.error('Error checking token validity:', error);
        return false;
      }
    },

    async getToken(): Promise<string | null> {
      const isServiceWorker = typeof window === 'undefined';
      
      // If we have in-memory token, use it first (prioritize recent login tokens)
      if (inMemoryAccessToken) {
        try {
          const payload = JSON.parse(atob(inMemoryAccessToken.split('.')[1]));
          const expirationTime = payload.exp * 1000;
          if (Date.now() < expirationTime) {
            return inMemoryAccessToken;
          }
        } catch (error) {
          // Token is malformed, continue to fetch from IndexedDB
          console.warn('Invalid in-memory token, fetching from IndexedDB');
        }
      }
      
      try {
        let user;
        if(isServiceWorker) {
          user = await this.getUserAsync().then((user) => user);
        } else {
          user = await this.getUser();
        }

        if (!user) return null;
        const accessToken = isServiceWorker ? await getAccessTokenFromDB(user.role) : inMemoryAccessToken;
        
        // Update in-memory token with what we retrieved
        if (accessToken) {
          inMemoryAccessToken = accessToken;
        }
        // Return the in-memory token
        if (inMemoryAccessToken || accessToken) {
          const accessTokenValue = typeof window === 'undefined' ? accessToken : inMemoryAccessToken;

          const payload = JSON.parse(atob(accessTokenValue?.split('.')[1] || ''));
          const expirationTime = payload.exp * 1000;
          const timeUntilExpiry = expirationTime - Date.now();
          
          // If token expires in less than 30 seconds, refresh in background
          if (timeUntilExpiry < 30000 && timeUntilExpiry > 0) {
            // Don't await the refresh, let it happen in background
            this.refreshAccessToken().catch(() => {});
          }
        }
        return inMemoryAccessToken || accessToken;
      } catch (error) {
        console.error('Error in getToken:', error);
        return inMemoryAccessToken;
      }
    },

    async getRefreshToken(): Promise<string | null> {
      if (!inMemoryCurrentRole) return null;
      return getRefreshTokenFromDB(inMemoryCurrentRole);
    },

    getUser(): { id: string; name: string; email: string; role: string; gender: string; mobileNumber: string; } | null {
      return inMemoryUser || null;
    },

    async getUserAsync(): Promise<{ id: string; name: string; email: string; role: string; gender: string; mobileNumber: string; } | null> {
      
      if(typeof window !== 'undefined') {
        return inMemoryUser || null;
      }

      if(!inMemoryUser) {
        inMemoryUser = await getUserDataFromDB('user');
      }

      return inMemoryUser;
    },

    getCurrentRole(): string | null {
      return inMemoryCurrentRole;
    },

    async refreshAccessToken(): Promise<string | null> {
      if (authServiceStatic.refreshPromise) {
        return authServiceStatic.refreshPromise;
      }

      authServiceStatic.refreshPromise = refreshToken();
      
      try {
        const result = await authServiceStatic.refreshPromise;
        if (!result) {
          // If refresh failed but we still have a valid current token, don't logout
          if (inMemoryAccessToken) {
            try {
              const payload = JSON.parse(atob(inMemoryAccessToken.split('.')[1]));
              if (payload.exp * 1000 > Date.now()) {
                return inMemoryAccessToken;
              }
            } catch (error) {
              console.error('Error parsing current token:', error);
            }
          }
        }
        return result;
      } finally {
        authServiceStatic.refreshPromise = null;
      }
    },

    handleTokenExpiration(error: any): Promise<string | null> {
      // Don't handle token expiration for password reset flow
      if (typeof window !== 'undefined' && window.location.pathname.includes('/reset-password')) {
        throw error;
      }

      if (
        error?.response?.status === 401 || 
        error?.message?.includes('token') ||
        error?.message?.includes('unauthorized')
      ) {
        return this.refreshAccessToken();
      }
      throw error;
    },

    async isTokenExpired(): Promise<boolean> {
      if (!inMemoryAccessToken) return true;
      
      try {
        const payload = JSON.parse(atob(inMemoryAccessToken.split('.')[1]));
        const expirationTime = payload.exp * 1000;
        const currentTime = Date.now();
        const timeUntilExpiry = expirationTime - currentTime;
        
        if (timeUntilExpiry < 10000) {
          if (timeUntilExpiry > 0) {
            this.refreshAccessToken().catch(() => {});
          }
          return true;
        }
        
        return false;
      } catch (error) {
        console.error('Error checking token expiration:', error);
        return true;
      }
    },

    async forgotPassword(email: string): Promise<ForgotPasswordResponse> {
      try {
        return await api.fetch<ForgotPasswordResponse>('/auth/forgot-password', {
          method: 'POST',
          body: JSON.stringify({ email }),
          requiresAuth: false
        });
      } catch (error) {
        console.error('Forgot password error:', error);
        throw error;
      }
    },

    async changePassword(passwordData: ChangePasswordRequest): Promise<ChangePasswordResponse> {
      try {
        return await api.fetch<ChangePasswordResponse>(`/auth/change-password/${passwordData.id}`, {
          method: 'PUT',
          body: JSON.stringify({
            currentPassword: passwordData.currentPassword,
            newPassword: passwordData.newPassword,
            confirmPassword: passwordData.confirmPassword
          })
        });
      } catch (error) {
        console.error('Change password error:', error);
        throw error;
      }
    },

    async resetPassword(resetData: ResetPasswordRequest): Promise<ResetPasswordResponse> {
      try {
        return await api.fetch<ResetPasswordResponse>('/auth/reset-password', {
          method: 'POST',
          body: JSON.stringify(resetData),
          requiresAuth: false
        });
      } catch (error) {
        console.error('Reset password error:', error);
        // Don't redirect on reset password failure
        throw error;
      }
    },

    async generateOtp(userId: string): Promise<GenerateOtpResponse> {
      try {
        const request: GenerateOtpRequest = { userId };
        const res = await api.fetch<GenerateOtpResponse>('/auth/generate-otp', {
          method: 'POST',
          body: JSON.stringify(request),
          requiresAuth: false,
          skipRefreshToken: true
        });
        
        return res
      } catch (error) {
        console.error('Generate OTP error:', error);
        throw error;
      }
    },

    async enableTwoFactorAuth(userId: string, is2FAEnabled: boolean): Promise<{ message: string }> {
      try {
        return await api.fetch<{ message: string }>('/two-factor-auth/enable', {
          method: 'POST',
          body: JSON.stringify({
            userId,
            is2FAEnabled
          })
        });
      } catch (error) {
        console.error('Enable Two-Factor Authentication error:', error);
        throw error;
      }
    },

    async verifyOtp(userId: string, otp: string): Promise<VerifyOtpResponse> {
      try {
        const request: VerifyOtpRequest = { userId, otp };
        const data = await api.fetch<VerifyOtpResponse>('/auth/verify-otp', {
          method: 'POST',
          body: JSON.stringify(request),
          requiresAuth: false
        });

        if (data.accessToken && data.refreshToken && data.user) {
          // Save all data
          await saveRefreshToken(data.refreshToken, data.user.role, data.user);
          await saveAccessToken(data.accessToken, data.user.role);

          // Update in-memory state after saves are complete
          inMemoryAccessToken = data.accessToken;
          inMemoryUser = data.user;
          inMemoryCurrentRole = data.user.role;

          // Dispatch event to notify components
          syncWithAuthContext();

          // Broadcast login event to all other tabs using localStorage
          if (typeof window !== 'undefined') {
            localStorage.setItem('auth-event', JSON.stringify({
              type: 'LOGIN',
              timestamp: Date.now()
            }));
            localStorage.removeItem('auth-event');
          }

          // Navigate to the appropriate page based on role after all operations are complete
          const path = this.getRolePath(data.user.role);
          navigateTo(path);

        }

        return data;
      } catch (error) {
        console.error('Verify OTP error:', error);
        throw error;
      }
    },

    async verifyResetToken(token: string): Promise<VerifyResetTokenResponse> {
      try {
        return await api.fetch<VerifyResetTokenResponse>(`/auth/verify-reset-token?token=${token}`, {
          method: 'GET',
          requiresAuth: false
        });
      } catch (error) {
        console.error('Verify reset token error:', error);
        // Don't redirect on reset token verification failure
        throw error;
      }
    },
    
    // Add a listener for auth state changes
    onAuthStateChange(callback: () => void): () => void {
      window.addEventListener(AUTH_STATE_CHANGE_EVENT, callback);
      return () => window.removeEventListener(AUTH_STATE_CHANGE_EVENT, callback);
    },
    
    // Method to get the current auth state for components
    getAuthState() {
      return {
        accessToken: inMemoryAccessToken,
        user: inMemoryUser,
        currentRole: inMemoryCurrentRole
      };
    },

    // Initialize auth state from refresh token
    async initializeAuthState(): Promise<boolean> {
      try {
        // Check if we have any refresh tokens in IndexedDB
        const db = await openDB(AUTH_DATABASE_NAME, dbVersion, {
          upgrade(db: any) {
            if (!db.objectStoreNames.contains(AUTH_STORE_NAME)) {
              db.createObjectStore(AUTH_STORE_NAME, { keyPath: 'key' });
            }
          },
        });
        
        // Get all keys from the store
        const tx = db.transaction(AUTH_STORE_NAME, 'readonly');
        const store = tx.objectStore(AUTH_STORE_NAME);
        const keys = await store.getAllKeys();
        
        // Find any refresh token keys (they end with _refreshToken)
        const refreshTokenKeys = keys.filter((key: any) => 
          typeof key === 'string' && key.endsWith('_refreshToken')
        );
        
        if (refreshTokenKeys.length === 0) {
          return false; // No refresh tokens found
        }
        
        // Extract the role from the first refresh token key
        const role = String(refreshTokenKeys[0]).split('_refreshToken')[0];
        
        // Set the current role so refreshToken() can work
        inMemoryCurrentRole = role;
        
        // Get user data from IndexedDB
        inMemoryUser = await getUserRoleDataFromDB(role);
        
        // Try to refresh the token
        const newToken = await this.refreshAccessToken();
        
        // If we got a new token, we're authenticated
        return !!newToken;
      } catch (error) {
        console.error('Error initializing auth state:', error);
        return false;
      }
    }
  };

  return authService;
};

export const authService = createAuthService();
